import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Upload, Trash2, Plus, X } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import { supabase } from '@/integrations/supabase/client'
import { getQuestionImagePublicUrl } from '@/utils/imageUtils'
import { RichTextEditor } from '@/components/ui/rich-text-editor'

interface QuestionOption {
  id: string
  label: string
  text: string
}

export type QuestionFormWithImageProps = {
  testId?: string | null
  initialData?: {
    id?: string
    text: string
    category: string
    choices: {
      label: string
      text: string
      is_correct: boolean
    }[]
    image_path?: string
  }
  onSuccess?: () => void
}

export default function QuestionFormWithImage({
  testId,
  initialData,
  onSuccess,
}: QuestionFormWithImageProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialize state with initialData if provided
  const [text, setText] = useState<string>(initialData?.text ?? '')
  const [category, setCategory] = useState<string>(initialData?.category ?? '')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  // Initialize options with initial choices or default options
  const [options, setOptions] = useState<QuestionOption[]>(
    initialData?.choices
      ? initialData.choices.map((c) => ({
          id: c.label,
          label: c.label,
          text: c.text,
        }))
      : [
          { id: 'A', label: 'A', text: '' },
          { id: 'B', label: 'B', text: '' },
          { id: 'C', label: 'C', text: '' },
          { id: 'D', label: 'D', text: '' },
        ]
  )

  // Set correct answer from initial data
  const [correctAnswerId, setCorrectAnswerId] = useState<string>(
    initialData?.choices.find((c) => c.is_correct)?.label ?? ''
  )

  useEffect(() => {
    if (initialData?.image_path) {
      const publicUrl = getQuestionImagePublicUrl(initialData.image_path)
      setImageUrl(publicUrl)
      setImagePreview(publicUrl)
    }
  }, [initialData?.image_path])

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Dosya çok büyük',
        description: 'Lütfen 5MB altı bir resim seçin.',
        variant: 'destructive',
      })
      return
    }
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Geçersiz dosya türü',
        description: 'Lütfen bir resim dosyası seçin.',
        variant: 'destructive',
      })
      return
    }
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = () => setImagePreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const updateOption = (idx: number, newText: string) => {
    const arr = [...options]
    arr[idx].text = newText
    setOptions(arr)
  }

  const addOption = () => {
    const next = String.fromCharCode(65 + options.length)
    setOptions([...options, { id: next, label: next, text: '' }])
  }

  const removeOption = (idx: number) => {
    const opt = options[idx]
    setOptions(options.filter((_, i) => i !== idx))
    if (opt.id === correctAnswerId) setCorrectAnswerId('')
  }

  const validateForm = () => {
    // Strip HTML tags to check if there's actual text content
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = text
    const plainText = tempDiv.textContent || tempDiv.innerText || ''

    if (!plainText.trim()) {
      toast({
        title: 'Eksik bilgi',
        description: 'Soru metni gereklidir.',
        variant: 'destructive',
      })
      return false
    }
    if (options.some((opt) => !opt.text.trim())) {
      toast({
        title: 'Eksik bilgi',
        description: 'Tüm seçenekler doldurulmalıdır.',
        variant: 'destructive',
      })
      return false
    }
    if (!correctAnswerId) {
      toast({
        title: 'Eksik bilgi',
        description: 'Doğru cevap seçmelisiniz.',
        variant: 'destructive',
      })
      return false
    }
    return true
  }

  const handleImageUpdate = async (action: 'upload' | 'remove', file?: File) => {
    try {
      // Handle remove action
      if (action === 'remove' && initialData?.id) {
        // Send delete request to backend
        const res = await authedFetch(`${API_BASE_URL}/api/questions/${initialData.id}/image`, {
          method: 'DELETE',
        })

        if (!res.ok) {
          toast({
            title: 'Hata',
            description: 'Görsel kaldırılırken bir hata oluştu.',
          })
        }

        // Clear all image states
        setImageFile(null)
        setImagePreview(null)
        setImageUrl(null)
        if (fileInputRef.current) fileInputRef.current.value = ''

        toast({
          title: 'Başarılı',
          description: 'Görsel kaldırıldı.',
        })

        return null
      }

      // Handle upload action
      if (action === 'upload' && file) {
        const formData = new FormData()
        formData.append('image', file)

        // If updating existing question
        if (initialData?.id) {
          const res = await authedFetch(`${API_BASE_URL}/api/questions/${initialData.id}/image`, {
            method: 'PUT',
            body: formData,
          })

          if (!res.ok) {
            toast({
              title: 'Hata',
              description: 'Yeni görsel yüklenirken bir hata oluştu.',
              variant: 'destructive',
            })
          }

          const data = await res.json()
          return data.image_path
        }

        // For new questions, return the file to be handled by createQuestion
        return file
      }

      return null
    } catch (error) {
      console.error('Error handling image:', error)
      toast({
        title: 'Hata',
        description: 'Görsel işlenirken bir hata oluştu.',
        variant: 'destructive',
      })
      return null
    }
  }

  const choicesChanged = () => {
    if (!initialData?.choices) return true

    // Compare by label to handle order changes
    const initialLabels = new Set(initialData.choices.map((c) => c.label))
    const currentLabels = new Set(options.map((o) => o.label))

    // Check if sets differ
    if (
      initialLabels.size !== currentLabels.size ||
      ![...initialLabels].every((label) => currentLabels.has(label))
    ) {
      return true
    }

    // Check only if text changed for existing choices (not correctness)
    return options.some((opt) => {
      const initial = initialData.choices.find((c) => c.label === opt.label)
      if (!initial) return true // New choice
      return opt.text !== initial.text
    })
  }

  const updateQuestionWithChoices = async (questionId: string, imagePath: string | null) => {
    // Update the question
    const { error: questionError } = await supabase
      .from('questions')
      .update({
        text,
        category: category || 'Diğer',
        test_id: testId || null,
        image_path: imagePath,
        updated_at: new Date().toISOString(),
      })
      .eq('id', questionId)

    if (questionError) throw questionError

    // Only handle choices if text changed
    if (!choicesChanged()) return

    // Upsert remaining choices - only update text
    const updatePromises = options.map((opt) =>
      supabase.from('choices').upsert(
        {
          question_id: questionId,
          label: opt.label,
          text: opt.text,
          is_correct: initialData?.choices.find((c) => c.label === opt.label)?.is_correct ?? false,
        },
        { onConflict: 'question_id,label' }
      )
    )

    const results = await Promise.all(updatePromises)
    const choicesError = results.find((r) => r.error)?.error
    if (choicesError) throw choicesError
  }

  const createQuestion = async () => {
    const formData = new FormData()
    formData.append('text', text)
    formData.append(
      'choices',
      JSON.stringify(
        options.map((o) => ({
          label: o.label,
          text: o.text,
          is_correct: o.id === correctAnswerId,
        }))
      )
    )
    formData.append('category', category || 'Diğer')
    if (testId) formData.append('test_id', testId)
    if (imageFile) formData.append('image', imageFile)

    const res = await authedFetch(`${API_BASE_URL}/api/questions`, {
      method: 'POST',
      body: formData,
    })

    if (!res.ok) {
      const errJson = await res.json()
      throw new Error(errJson.detail || res.statusText)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    // Check if correct answer changed
    const oldCorrectAnswer = initialData?.choices.find((c) => c.is_correct)?.label
    if (initialData?.id && oldCorrectAnswer && oldCorrectAnswer !== correctAnswerId) {
      toast({
        title: 'Değişiklik Reddedildi',
        description:
          'Doğru cevap değiştirilemez. Sadece soru metni, kategori ve seçenek metinleri güncellenebilir.',
        variant: 'destructive',
      })
      setCorrectAnswerId(oldCorrectAnswer)
      return
    }

    try {
      if (initialData?.id) {
        let finalImagePath = initialData.image_path
        // Only handle image update if there's a new file or if image was explicitly removed
        if (imageFile) {
          // Upload new image
          finalImagePath = await handleImageUpdate('upload', imageFile)
        } else if (imagePreview === null && initialData.image_path) {
          // Image was explicitly removed
          await handleImageUpdate('remove')
          finalImagePath = null
        }
        // Update question with the final image path
        await updateQuestionWithChoices(initialData.id, finalImagePath)

        // Refresh image URL after update if question has an image
        if (finalImagePath) {
          const publicUrl = getQuestionImagePublicUrl(finalImagePath)
          setImageUrl(publicUrl)
          setImagePreview(publicUrl)
        }
      } else {
        await createQuestion()
      }

      toast({
        title: 'Başarılı!',
        description: initialData?.id ? 'Soru güncellendi.' : 'Soru eklendi.',
      })

      onSuccess?.()
    } catch (error: any) {
      toast({
        title: 'Hata',
        description: error?.message || 'Bilinmeyen bir hata oluştu.',
        variant: 'destructive',
      })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4">
        <div>
          <Label htmlFor="questionText">Soru Metni</Label>
          <RichTextEditor
            content={text}
            onChange={setText}
            placeholder="Sorunuzu buraya yazın..."
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="category">Kategori</Label>
          <Input
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Kategori (opsiyonel)"
          />
        </div>
        <div>
          <Label>Soru Görseli (Opsiyonel)</Label>
          {!imagePreview && !imageUrl ? (
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2"
              >
                <Upload /> Görsel Yükle
              </Button>
            </div>
          ) : (
            <Card>
              <CardContent className="relative p-4">
                <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                  <img
                    src={imagePreview || imageUrl || ''}
                    alt="Soru görseli"
                    className="object-contain w-full h-full"
                    onError={(e) => {
                      console.error('Image load failed')
                      e.currentTarget.src = '/placeholder-image.png'
                    }}
                  />
                </div>
                <Button
                  size="sm"
                  type="button"
                  variant="destructive"
                  className="absolute top-2 right-2"
                  onClick={async () => {
                    await handleImageUpdate('remove')
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
        <div>
          <Label>Cevap Seçenekleri *</Label>
          <div className="space-y-2 mt-2">
            {options.map((o, i) => (
              <div key={o.id} className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-100 flex items-center justify-center rounded-full text-sm font-medium">
                  {o.label}
                </div>
                <Input
                  value={o.text}
                  onChange={(e) => updateOption(i, e.target.value)}
                  placeholder={`Seçenek ${o.label}`}
                  className="flex-1"
                />
                {options.length > 2 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => removeOption(i)}>
                    <Trash2 />
                  </Button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <Button
                type="button"
                variant="outline"
                onClick={addOption}
                className="flex items-center gap-2"
              >
                <Plus /> Seçenek Ekle
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="correctAnswer">Doğru Cevap *</Label>
          <select
            id="correctAnswer"
            value={correctAnswerId}
            onChange={(e) => setCorrectAnswerId(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Seçiniz...</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label} – {o.text || 'Boş'}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Button type="submit" className="w-full">
        {initialData?.id ? 'Soruyu Güncelle' : 'Soru Ekle'}
      </Button>
    </form>
  )
}
